import { useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { useBookmarks, useCompletedChaptersCount } from '../api/useManga'
import { useLogout, useUpdateProfile, useUploadAvatar, useChangePassword, useDeleteAccount } from '../api/useAuth'
import { mediaUrl } from '../utils/media'
import { getErrMsg } from '../api/client'
import ConfirmModal from '../components/ui/ConfirmModal'
import Adult18Modal from '../components/ui/Adult18Modal'
import toast from 'react-hot-toast'

export default function Profile() {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const { mutate: logoutMutate } = useLogout()
  const { mutate: updateProfile, isPending: saving } = useUpdateProfile()
  const { mutate: uploadAvatar, isPending: uploadingAvatar } = useUploadAvatar()
  const { mutate: changePassword, isPending: changingPwd } = useChangePassword()
  const { mutate: deleteAccount, isPending: deletingAccount } = useDeleteAccount()
  const { data: bookmarks } = useBookmarks()
  const { data: readChaptersCount = 0 } = useCompletedChaptersCount()

  const [username, setUsername] = useState(user?.username || '')
  const [email, setEmail] = useState(user?.email || '')
  const [editing, setEditing] = useState(false)
  const [pwdOpen, setPwdOpen] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [adult18Confirm, setAdult18Confirm] = useState(false)
  const [currentPwd, setCurrentPwd] = useState('')
  const [newPwd, setNewPwd] = useState('')
  const [confirmPwd, setConfirmPwd] = useState('')

  const fileInputRef = useRef(null)
  const readTitles = bookmarks?.length || 0
  const avatarSrc = mediaUrl(user?.avatar_url)

  const handleLogout = () => {
    logoutMutate(undefined, {
      onSuccess: () => { toast.success('Вы вышли из аккаунта'); navigate('/') },
    })
  }

  const handleSaveProfile = () => {
    const trimmedUsername = username.trim()
    const trimmedEmail = email.trim()
    if (!trimmedUsername || !trimmedEmail) return
    updateProfile({ username: trimmedUsername, email: trimmedEmail }, {
      onSuccess: () => { toast.success('Профиль обновлён'); setEditing(false) },
      onError: (err) => toast.error(getErrMsg(err, 'Ошибка сохранения')),
    })
  }

  const handleCancelEdit = () => { setUsername(user?.username || ''); setEmail(user?.email || ''); setEditing(false) }

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    uploadAvatar(file, {
      onSuccess: () => toast.success('Аватар обновлён'),
      onError: () => toast.error('Ошибка загрузки аватара'),
    })
    e.target.value = ''
  }

  const handleChangePassword = () => {
    if (!currentPwd || !newPwd || !confirmPwd) return
    if (newPwd !== confirmPwd) { toast.error('Пароли не совпадают'); return }
    if (newPwd.length < 8) { toast.error('Пароль должен содержать минимум 8 символов'); return }
    changePassword({ current_password: currentPwd, new_password: newPwd }, {
      onSuccess: () => {
        toast.success('Пароль изменён')
        setCurrentPwd(''); setNewPwd(''); setConfirmPwd('')
        setPwdOpen(false)
      },
      onError: (err) => toast.error(getErrMsg(err, 'Ошибка смены пароля')),
    })
  }

  const handleCancelPwd = () => { setCurrentPwd(''); setNewPwd(''); setConfirmPwd(''); setPwdOpen(false) }

  const handleDeleteAccount = () => {
    deleteAccount(undefined, {
      onSuccess: () => { toast.success('Аккаунт удалён'); navigate('/') },
      onError: (err) => toast.error(getErrMsg(err, 'Ошибка удаления')),
    })
  }

  const isFrozen = user?.status === 'frozen'

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
      {isFrozen && (
        <div className="mb-5 p-4 rounded-xl border border-blue-400/30 bg-blue-400/10 flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-blue-400 mb-1">🧊 Аккаунт заморожен</p>
            {user.freeze_reason && (
              <p className="text-sm text-fg"><span className="text-muted">Причина:</span> {user.freeze_reason}</p>
            )}
            {user.freeze_until
              ? <p className="text-sm text-muted mt-0.5">До: {new Date(user.freeze_until).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
              : <p className="text-sm text-muted mt-0.5">Навсегда</p>
            }
            <p className="text-sm text-muted mt-1">Для обжалования обратитесь в поддержку.</p>
          </div>
          <Link to="/support"
            className="shrink-0 px-3 py-1.5 rounded-lg text-sm font-semibold bg-blue-400/20 text-blue-400 hover:bg-blue-400/30 transition-colors">
            Поддержка
          </Link>
        </div>
      )}
      <div className="rounded-2xl overflow-hidden bg-surface border border-line">

        {/* Header */}
        <div className="p-6 sm:p-8 flex items-center gap-5">
          <button onClick={() => fileInputRef.current?.click()} disabled={uploadingAvatar}
            className="relative shrink-0" title="Сменить фото профиля">
            <div className="w-20 h-20 rounded-2xl overflow-hidden flex items-center justify-center text-2xl font-black bg-accent-dim text-accent">
              {uploadingAvatar
                ? <span className="animate-spin text-base inline-block">↻</span>
                : avatarSrc
                  ? <img src={avatarSrc} alt="avatar" className="w-full h-full object-cover" />
                  : <span>{user?.username?.slice(0, 2).toUpperCase()}</span>
              }
            </div>
            <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center text-xs bg-accent text-on-accent">
              ✎
            </div>
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} />

          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-black truncate">{user?.username}</h1>
            <p className="text-sm mt-0.5 truncate text-muted">{user?.email}</p>
            <span className="inline-block mt-2 px-2 py-0.5 rounded-md text-xs font-semibold bg-accent-dim text-accent">
              {user?.role === 'admin' ? 'Администратор' : 'Пользователь'}
            </span>
          </div>
        </div>

        {/* Edit form */}
        {editing && (
          <div className="px-6 sm:px-8 pb-6 border-t border-line">
            <p className="text-sm font-semibold mt-5 mb-3">Редактировать профиль</p>
            <div className="flex flex-col gap-3">
              <ProfileField label="Имя пользователя">
                <input value={username} onChange={(e) => setUsername(e.target.value)}
                  className="px-3 py-2 rounded-xl text-sm outline-none w-full bg-surface2 text-fg border border-line focus:border-accent transition-colors" />
              </ProfileField>
              <ProfileField label="Email">
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="px-3 py-2 rounded-xl text-sm outline-none w-full bg-surface2 text-fg border border-line focus:border-accent transition-colors" />
              </ProfileField>
              <div className="flex gap-2 mt-1">
                <button onClick={handleSaveProfile} disabled={saving}
                  className="px-5 py-2 rounded-xl text-sm font-bold bg-accent text-on-accent disabled:opacity-60 hover:opacity-90 transition-opacity">
                  {saving ? 'Сохранение…' : 'Сохранить'}
                </button>
                <button onClick={handleCancelEdit}
                  className="px-5 py-2 rounded-xl text-sm font-semibold bg-surface2 text-muted hover:text-fg transition-colors">
                  Отмена
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="border-t border-line" />

        {/* Password */}
        <div className="px-6 sm:px-8 py-5">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold">Сменить пароль</p>
            {!pwdOpen && (
              <button onClick={() => setPwdOpen(true)}
                className="text-sm px-4 py-1.5 rounded-xl font-semibold bg-surface2 text-fg hover:bg-surface3 transition-colors">
                Изменить
              </button>
            )}
          </div>
          {pwdOpen && (
            <div className="flex flex-col gap-3 mt-4">
              {[
                { label: 'Текущий пароль', value: currentPwd, onChange: setCurrentPwd },
                { label: 'Новый пароль', value: newPwd, onChange: setNewPwd },
                { label: 'Повторите новый пароль', value: confirmPwd, onChange: setConfirmPwd },
              ].map(({ label, value, onChange }) => (
                <ProfileField key={label} label={label}>
                  <input type="password" value={value} onChange={(e) => onChange(e.target.value)}
                    onKeyDown={label.includes('Повторите') ? (e) => e.key === 'Enter' && handleChangePassword() : undefined}
                    className="px-3 py-2 rounded-xl text-sm outline-none w-full bg-surface2 text-fg border border-line focus:border-accent transition-colors" />
                </ProfileField>
              ))}
              <div className="flex gap-2 mt-1">
                <button onClick={handleChangePassword} disabled={changingPwd}
                  className="px-5 py-2 rounded-xl text-sm font-bold bg-accent text-on-accent disabled:opacity-60 hover:opacity-90 transition-opacity">
                  {changingPwd ? 'Сохранение…' : 'Сохранить'}
                </button>
                <button onClick={handleCancelPwd}
                  className="px-5 py-2 rounded-xl text-sm font-semibold bg-surface2 text-muted hover:text-fg transition-colors">
                  Отмена
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-line" />

        {/* Content preferences */}
        <div className="px-6 sm:px-8 py-5 flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold">Контент 18+</p>
            <p className="text-xs text-muted mt-0.5">Показывать материалы для взрослых в каталоге</p>
          </div>
          <button
            onClick={() => user?.show_adult ? updateProfile({ show_adult: false }) : setAdult18Confirm(true)}
            disabled={saving}
            className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${user?.show_adult ? 'bg-accent' : 'bg-surface2 border border-line'}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full transition-transform bg-fg ${user?.show_adult ? 'translate-x-6' : 'translate-x-0'}`} />
          </button>
        </div>

        <div className="border-t border-line" />

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 px-6 sm:px-8 py-6">
          <StatCard label="Прочитано тайтлов" value={readTitles} />
          <StatCard label="Прочитано глав" value={readChaptersCount} />
        </div>

        <div className="border-t border-line" />

        {/* Nav links */}
        <div className="flex flex-col gap-1 px-6 sm:px-8 py-4">
          <NavLink to="/bookmarks">Мои закладки</NavLink>
          <NavButton onClick={() => setEditing((v) => !v)}>
            {editing ? 'Скрыть редактирование' : 'Редактировать профиль'}
          </NavButton>
        </div>

        <div className="border-t border-line" />

        {/* Logout + Delete */}
        <div className="px-6 sm:px-8 py-6 flex flex-col gap-3">
          <button onClick={handleLogout}
            className="w-full py-3 rounded-xl text-sm font-bold border bg-red-600/10 text-red-400 border-red-600/20 hover:bg-red-600/18 transition-colors">
            Выйти из аккаунта
          </button>
          <button onClick={() => setDeleteConfirm(true)}
            className="w-full py-2 rounded-xl text-xs font-semibold text-muted hover:text-red-400 transition-colors">
            Удалить аккаунт
          </button>
          <ConfirmModal
            isOpen={deleteConfirm}
            onClose={() => setDeleteConfirm(false)}
            onConfirm={handleDeleteAccount}
            title="Удалить аккаунт?"
            message="Это действие необратимо — все данные, закладки и история чтения будут удалены."
            confirmLabel="Да, удалить"
            isPending={deletingAccount}
            danger
          />
          <Adult18Modal
            isOpen={adult18Confirm}
            onClose={() => setAdult18Confirm(false)}
            onConfirm={() => { setAdult18Confirm(false); updateProfile({ show_adult: true }) }}
          />
        </div>
      </div>
    </div>
  )
}

function ProfileField({ label, children }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs text-muted">{label}</span>
      {children}
    </label>
  )
}

function StatCard({ label, value }) {
  return (
    <div className="rounded-xl p-4 flex flex-col gap-1 bg-surface2">
      <p className="text-2xl font-black text-accent">{value}</p>
      <p className="text-xs text-muted">{label}</p>
    </div>
  )
}

function NavLink({ to, children }) {
  return (
    <Link to={to}
      className="flex items-center justify-between px-4 py-3 rounded-xl transition-colors bg-surface2 hover:bg-surface3">
      <span className="font-semibold text-sm">{children}</span>
      <span className="text-muted">→</span>
    </Link>
  )
}

function NavButton({ onClick, children }) {
  return (
    <button onClick={onClick}
      className="flex items-center justify-between px-4 py-3 rounded-xl transition-colors w-full text-left bg-surface2 hover:bg-surface3">
      <span className="font-semibold text-sm">{children}</span>
      <span className="text-muted">→</span>
    </button>
  )
}
